import { type Dayjs } from 'dayjs';
import {
  type KeyboardEvent,
  type MouseEvent,
  type RefObject,
  useEffect,
  useRef,
  useState,
} from 'react';
import { getScrollableAncestors } from '@/ui/composites/grid/scrollRetention';
import {
  defaultTableToolbarDateQuickFilter,
  resolveTableToolbarStateModel,
} from './tableToolbarStateModel';
import type { DateQuickFilter } from '@/features/shared/hooks/useDateWindow';
import type { GridColumnVisibilityModel } from '@/ui/primitives';

export type TableToolbarPopover = 'columns' | 'filters' | 'date' | 'sort' | null;
export type ToolbarHoverTarget =
  | 'columns'
  | 'filters'
  | 'date'
  | 'sort'
  | 'search'
  | 'reset'
  | `quickSort:${string}`
  | null;

type TableToolbarStateParams<TSortKey extends string, TStatus extends string> = {
  searchValue?: string;
  onDebouncedSearchChange: (value: string) => void;
  columnVisibilityModel: GridColumnVisibilityModel;
  defaultColumnVisibilityModel: GridColumnVisibilityModel;
  columnOptions: Array<{ field: string; mandatory?: boolean }>;
  onColumnVisibilityModelChange: (nextModel: GridColumnVisibilityModel) => void;
  sortKey: TSortKey;
  sortDirection: 'asc' | 'desc';
  defaultSortKey: TSortKey;
  defaultSortDirection: 'asc' | 'desc';
  onSortChange: (sortKey: TSortKey, sortDirection: 'asc' | 'desc') => void;
  filterStatuses: TStatus[];
  onFilterStatusesChange: (next: TStatus[]) => void;
  filterEntities?: string[];
  onFilterEntitiesChange?: (next: string[]) => void;
  filterItems?: string[];
  onFilterItemsChange?: (next: string[]) => void;
  filterSuites: string[];
  onFilterSuitesChange?: (next: string[]) => void;
  filterTags: string[];
  onFilterTagsChange: (next: string[]) => void;
  dateQuickFilter: DateQuickFilter;
  onDateQuickFilterChange: (next: DateQuickFilter) => void;
  fromDate: Dayjs | null;
  toDate: Dayjs | null;
  latestQueryStartDay?: string | null;
  latestQueryEndDay?: string | null;
  onFromDateChange: (next: Dayjs | null) => void;
  onToDateChange: (next: Dayjs | null) => void;
  summaryCount?: number | null;
  summarySingularLabel?: string;
  summaryPluralLabel?: string;
  previewFromDate?: Dayjs | null;
  previewToDate?: Dayjs | null;
};

export type TableToolbarStateResult = {
  popoverState: { type: TableToolbarPopover; anchorEl: HTMLElement | null };
  searchExpanded: boolean;
  searchValue: string;
  hoverTarget: ToolbarHoverTarget;
  searchInputRef: RefObject<HTMLInputElement | null>;
  hasNonDefaultColumns: boolean;
  hasActiveFilters: boolean;
  hasActiveSearch: boolean;
  hasActiveDateFilters: boolean;
  hasActiveSort: boolean;
  hasActiveToolbarState: boolean;
  dateRangeLabel: string | null;
  displayedFromDate: Dayjs | null;
  displayedToDate: Dayjs | null;
  setHoverTarget: (target: ToolbarHoverTarget) => void;
  setSearchValue: (value: string) => void;
  openSearch: () => void;
  handleSearchBlur: () => void;
  handleSearchKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
  openPopover: (
    type: Exclude<TableToolbarPopover, null>,
  ) => (event: MouseEvent<HTMLElement>) => void;
  closePopover: () => void;
  preserveWindowScroll: (action: () => void) => void;
  resetToolbarFilters: () => void;
};

export const useTableToolbarState = <TSortKey extends string, TStatus extends string>({
  searchValue: externalSearchValue = '',
  onDebouncedSearchChange,
  columnVisibilityModel,
  defaultColumnVisibilityModel,
  columnOptions,
  onColumnVisibilityModelChange,
  sortKey,
  sortDirection,
  defaultSortKey,
  defaultSortDirection,
  onSortChange,
  filterStatuses,
  onFilterStatusesChange,
  filterEntities = [],
  onFilterEntitiesChange,
  filterItems = [],
  onFilterItemsChange,
  filterSuites,
  onFilterSuitesChange,
  filterTags,
  onFilterTagsChange,
  dateQuickFilter,
  onDateQuickFilterChange,
  fromDate,
  toDate,
  latestQueryStartDay = null,
  latestQueryEndDay = null,
  onFromDateChange,
  onToDateChange,
  summaryCount = null,
  summarySingularLabel,
  summaryPluralLabel,
  previewFromDate = null,
  previewToDate = null,
}: TableToolbarStateParams<TSortKey, TStatus>): TableToolbarStateResult => {
  const [popoverState, setPopoverState] = useState<{
    type: TableToolbarPopover;
    anchorEl: HTMLElement | null;
  }>({
    type: null,
    anchorEl: null,
  });
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [searchValue, setSearchValue] = useState(externalSearchValue);
  const [hoverTarget, setHoverTarget] = useState<ToolbarHoverTarget>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const scrollRestoreTimeoutRef = useRef<number | null>(null);
  const scrollRestoreLongTimeoutRef = useRef<number | null>(null);
  const scrollRestoreRafRef = useRef<number | null>(null);
  const debouncedSearchChangeRef = useRef(onDebouncedSearchChange);

  useEffect(() => {
    debouncedSearchChangeRef.current = onDebouncedSearchChange;
  }, [onDebouncedSearchChange]);

  useEffect(() => {
    setSearchValue((currentValue) =>
      currentValue === externalSearchValue ? currentValue : externalSearchValue,
    );
  }, [externalSearchValue]);

  const openPopover =
    (type: Exclude<TableToolbarPopover, null>) => (event: MouseEvent<HTMLElement>) => {
      setPopoverState({ type, anchorEl: event.currentTarget });
    };

  const closePopover = (): void => {
    setPopoverState({ type: null, anchorEl: null });
  };

  const preserveWindowScroll = (action: () => void): void => {
    if (typeof window === 'undefined') {
      action();
      return;
    }
    const { scrollX, scrollY } = window;
    const activeElement =
      typeof document !== 'undefined' && document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    const ancestorSnapshots = getScrollableAncestors(activeElement);
    activeElement?.blur();
    const restoreScroll = (): void => {
      ancestorSnapshots.forEach(({ element, top, left }) => {
        element.scrollTop = top;
        element.scrollLeft = left;
      });
      window.scrollTo({ left: scrollX, top: scrollY, behavior: 'auto' });
    };
    action();
    window.requestAnimationFrame(() => {
      restoreScroll();
      scrollRestoreRafRef.current = window.requestAnimationFrame(restoreScroll);
    });
    if (scrollRestoreTimeoutRef.current !== null) {
      window.clearTimeout(scrollRestoreTimeoutRef.current);
    }
    scrollRestoreTimeoutRef.current = window.setTimeout(() => {
      restoreScroll();
      scrollRestoreTimeoutRef.current = null;
    }, 150);
    if (scrollRestoreLongTimeoutRef.current !== null) {
      window.clearTimeout(scrollRestoreLongTimeoutRef.current);
    }
    scrollRestoreLongTimeoutRef.current = window.setTimeout(() => {
      restoreScroll();
      scrollRestoreLongTimeoutRef.current = null;
    }, 500);
  };

  useEffect(() => {
    if (searchValue === externalSearchValue) {
      return;
    }
    const timer = window.setTimeout(() => {
      debouncedSearchChangeRef.current(searchValue);
    }, 300);
    return () => {
      window.clearTimeout(timer);
    };
  }, [externalSearchValue, searchValue]);

  useEffect(() => {
    if (!searchExpanded) return;
    window.setTimeout(() => {
      searchInputRef.current?.focus();
    }, 0);
  }, [searchExpanded]);

  useEffect(
    () => () => {
      if (scrollRestoreTimeoutRef.current !== null) {
        window.clearTimeout(scrollRestoreTimeoutRef.current);
      }
      if (scrollRestoreLongTimeoutRef.current !== null) {
        window.clearTimeout(scrollRestoreLongTimeoutRef.current);
      }
      if (scrollRestoreRafRef.current !== null) {
        window.cancelAnimationFrame(scrollRestoreRafRef.current);
      }
    },
    [],
  );

  const {
    dateRangeLabel,
    displayedFromDate,
    displayedToDate,
    hasActiveDateFilters,
    hasActiveFilters,
    hasActiveSearch,
    hasActiveSort,
    hasActiveToolbarState,
    hasNonDefaultColumns,
    mandatoryColumnVisibilityModel,
  } = resolveTableToolbarStateModel({
    columnOptions,
    columnVisibilityModel,
    dateQuickFilter,
    defaultColumnVisibilityModel,
    defaultSortDirection,
    defaultSortKey,
    filterEntities,
    filterItems,
    filterStatuses,
    filterSuites,
    filterTags,
    fromDate,
    latestQueryEndDay,
    latestQueryStartDay,
    previewFromDate,
    previewToDate,
    searchValue,
    sortDirection,
    sortKey,
    summaryCount,
    summaryPluralLabel,
    summarySingularLabel,
    toDate,
  });

  const resetToolbarFilters = (): void => {
    preserveWindowScroll(() => {
      setSearchValue('');
      setSearchExpanded(false);
      if (externalSearchValue.length > 0) {
        debouncedSearchChangeRef.current('');
      }
      onFilterStatusesChange([]);
      onFilterEntitiesChange?.([]);
      onFilterItemsChange?.([]);
      onFilterSuitesChange?.([]);
      onFilterTagsChange([]);
      onDateQuickFilterChange(defaultTableToolbarDateQuickFilter);
      onFromDateChange(null);
      onToDateChange(null);
      onSortChange(defaultSortKey, defaultSortDirection);
      onColumnVisibilityModelChange({
        ...defaultColumnVisibilityModel,
        ...mandatoryColumnVisibilityModel,
      });
      closePopover();
    });
  };

  const openSearch = (): void => {
    setSearchExpanded(true);
  };

  const handleSearchBlur = (): void => {
    if (searchValue.trim().length > 0) return;
    setSearchExpanded(false);
  };

  const handleSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key !== 'Escape') return;
    if (searchValue.length > 0) {
      setSearchValue('');
      return;
    }
    setSearchExpanded(false);
  };

  return {
    popoverState,
    searchExpanded,
    searchValue,
    hoverTarget,
    searchInputRef,
    hasNonDefaultColumns,
    hasActiveFilters,
    hasActiveSearch,
    hasActiveDateFilters,
    hasActiveSort,
    hasActiveToolbarState,
    dateRangeLabel,
    displayedFromDate,
    displayedToDate,
    setHoverTarget,
    setSearchValue,
    openSearch,
    handleSearchBlur,
    handleSearchKeyDown,
    openPopover,
    closePopover,
    preserveWindowScroll,
    resetToolbarFilters,
  };
};
